import { useLocation } from 'react-router-dom'

import { useChannelUrl } from './useChannelUrl'
import { useCommentsChannelUrl } from './useCommentsChannelUrl'
import { useInsightsUrl } from './useInsightsUrl'

/**
 * Build channel URL that maintains the current product context (Publish, Community, or Insights)
 *
 * This hook ensures that when navigating between channels, the current product
 * context is preserved:
 * - If in Insights (/insights or /insights/:channelId), returns an Insights channel URL
 * - If in Community (/community or /channels/:channelId/community), returns a Community channel URL
 * - If in Publish (/channels/:channelId), returns a Publish channel URL
 *
 * This allows channels to act as filters that maintain the user's current view,
 * rather than forcing them back to Publish when switching channels.
 *
 * @param channelId - The ID of the channel to navigate to
 * @returns URL string that maintains product context with preserved query parameters
 *
 * @example
 * ```tsx
 * // User is on /community?status=dismissed
 * const channelUrl = useContextAwareChannelUrl('channel-123')
 * // Returns: /community/channel-123?status=dismissed
 *
 * // User is on /channels/abc/community?status=dismissed
 * const channelUrl = useContextAwareChannelUrl('channel-123')
 * // Returns: /channels/channel-123/community?status=dismissed
 *
 * // User is on /channels/abc/queue?tags[]=tag1
 * const channelUrl = useContextAwareChannelUrl('channel-123')
 * // Returns: /channels/channel-123/queue?tags[]=tag1
 *
 * // User is on /insights?dateRange=last-month
 * const channelUrl = useContextAwareChannelUrl('channel-123')
 * // Returns: /insights/channel-123?dateRange=last-month
 * ```
 */
export function useContextAwareChannelUrl(channelId: string): string {
  const location = useLocation()
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const isInInsights = pathSegments[0] === 'insights'
  const isInCommunity = pathSegments.includes('community')

  const publishUrl = useChannelUrl(channelId)
  const communityUrl = useCommentsChannelUrl(channelId)
  const insightsUrl = useInsightsUrl(channelId)

  if (isInInsights) {
    return insightsUrl
  }

  return isInCommunity ? communityUrl : publishUrl
}
